import { useMutation, useQuery } from "@tanstack/react-query";
import { orgBillingTransferRecipientContract } from "@common/contracts/org-billing-transfer-recipient.contract";
import { billingQueryKeys } from "@hooks/useBillingStatus";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { isConflictError } from "./apiError";
import { queryClient } from "../queryClient";
import { organizationQueryKeys } from "./organization.api";
import { workspaceQueryKeys } from "./workspace.api";

const orgBillingTransferRecipientClient = getBackendAPIClient(
    orgBillingTransferRecipientContract,
);

export const orgBillingTransferRecipientQueryKeys = {
    all: () => ["org-billing-transfer-recipient"],
    pending: (workspaceId: string) => [
        ...orgBillingTransferRecipientQueryKeys.all(),
        "pending",
        workspaceId,
    ],
} as const;

interface PendingOrgBillingTransferQueryParams {
    workspaceId: string | null | undefined;
    enabled: boolean;
}

export function usePendingOrgBillingTransferQuery({
    workspaceId,
    enabled,
}: PendingOrgBillingTransferQueryParams) {
    return useQuery({
        queryKey: orgBillingTransferRecipientQueryKeys.pending(
            workspaceId ?? "",
        ),
        // A flag LaunchDarkly doesn't send is undefined at runtime, and React
        // Query treats `enabled: undefined` as enabled.
        enabled: !!enabled && !!workspaceId,
        queryFn: async () => {
            const response = await orgBillingTransferRecipientClient.getPending(
                {
                    query: { workspaceId: workspaceId ?? "" },
                },
            );
            return response.body.request;
        },
    });
}

function refetchPendingOnConflict(error: Error) {
    if (isConflictError(error)) {
        queryClient.invalidateQueries({
            queryKey: orgBillingTransferRecipientQueryKeys.all(),
        });
    }
}

export function useOrgBillingTransferCheckoutMutation() {
    return useMutation({
        mutationFn: async (requestId: string) => {
            const response = await orgBillingTransferRecipientClient.checkout({
                params: { requestId },
            });
            return response.body;
        },
        onError: refetchPendingOnConflict,
    });
}

interface AcceptOrgBillingTransferParams {
    requestId: string;
    intentId: string;
}

export function useAcceptOrgBillingTransferMutation() {
    return useMutation({
        mutationFn: async ({
            requestId,
            intentId,
        }: AcceptOrgBillingTransferParams) => {
            const response = await orgBillingTransferRecipientClient.accept({
                params: { requestId },
                body: { intentId },
            });
            return response.body;
        },
        onSuccess: () => {
            for (const queryKey of [
                workspaceQueryKeys.all(),
                billingQueryKeys.all(),
                organizationQueryKeys.all(),
                orgBillingTransferRecipientQueryKeys.all(),
            ]) {
                queryClient.invalidateQueries({ queryKey });
            }
        },
        onError: refetchPendingOnConflict,
    });
}

interface DeclineOrgBillingTransferParams {
    requestId: string;
    reason: string | undefined;
}

export function useDeclineOrgBillingTransferMutation() {
    return useMutation({
        mutationFn: async ({
            requestId,
            reason,
        }: DeclineOrgBillingTransferParams) => {
            const response = await orgBillingTransferRecipientClient.decline({
                extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
                params: { requestId },
                body: { reason },
            });
            return response.body;
        },
        onSuccess: () => {
            for (const queryKey of [
                workspaceQueryKeys.all(),
                orgBillingTransferRecipientQueryKeys.all(),
            ]) {
                queryClient.invalidateQueries({ queryKey });
            }
        },
        onError: refetchPendingOnConflict,
    });
}
